/*
 * BOMEX navigation sounds — the same early-2000s Flash-style cues as bomexaero.com
 * (src/components/site/NavigationSounds.tsx in aaron-lu/bomex-2-0), ported to plain JS.
 * Everything is synthesized with the Web Audio API, so the theme ships no audio files.
 *
 *   forward  rising chirp    — going deeper (a link, a category, a product)
 *   back     falling swoop   — going back up (parent link, Back, Close, Escape, browser Back)
 *   select   single blip     — buttons and options that stay on the same level
 *   folderOpen / folderClose — papery swish + thump for opening or leaving a folder
 *   flip     paper flick     — stepping through items inside a folder
 *
 * Markup can pick a cue with data-sound="forward|back|select|folder|flip" on a control or an
 * ancestor; data-sound="folder" opens or closes depending on direction. Visitors mute with the
 * corner toggle (snippets/bomex-sound-toggle.liquid); the choice is remembered in this browser.
 */
(() => {
  const tone = (frequency, at, length, wave = 'square', gain = 1, to = null) => ({ kind: 'tone', frequency, at, length, wave, gain, to });
  const noise = (at, length, filter, from, to, gain, q = 1) => ({ kind: 'noise', at, length, filter, from, to, gain, q });

  const cues = {
    // forward: bright rising three-note chirp
    forward: [tone(660, 0, 0.04), tone(880, 0.045, 0.04), tone(1320, 0.09, 0.07)],
    // back: lower, softer downward swoop with a short thud, clearly unlike the forward chirp
    back: [tone(620, 0, 0.16, 'triangle', 1.2, 180), tone(110, 0.12, 0.08, 'sine', 1.4)],
    select: [tone(880, 0, 0.04)],
    folderOpen: [noise(0, 0.12, 'bandpass', 700, 2600, 2.6, 0.9), tone(196, 0.0, 0.07, 'sine', 1.6), tone(1175, 0.1, 0.03, 'triangle', 0.7)],
    folderClose: [tone(1175, 0, 0.025, 'triangle', 0.6), noise(0.02, 0.11, 'bandpass', 2600, 700, 2.6, 0.9), tone(165, 0.08, 0.08, 'sine', 1.6)],
    flip: [noise(0, 0.03, 'highpass', 2600, 3400, 1.7), noise(0.038, 0.022, 'highpass', 3000, 3800, 0.9)],
  };

  const storageKey = 'bomex-sound-muted';
  const volume = 0.05;
  // One gesture can trigger several listeners (a click that also changes state); one cue is enough.
  const repeatWindowMs = 90;

  let context = null;
  let noiseBuffer = null;
  let lastCueAt = -Infinity;
  let lastUrl = new URL(window.location.href);
  let muted = readStoredMute();

  function readStoredMute() {
    try {
      return window.localStorage.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  }

  /** Schedules a cue on any AudioContext (also used by the offline preview renderer). */
  function renderCue(ctx, cue, start, destination, level = volume) {
    if (!noiseBuffer || noiseBuffer.sampleRate !== ctx.sampleRate) {
      noiseBuffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.5), ctx.sampleRate);
      const data = noiseBuffer.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    }
    // A little variation keeps repeated flips from sounding mechanical.
    const jitter = cue === 'flip' ? 0.85 + Math.random() * 0.3 : 1;
    for (const note of cues[cue]) {
      const at = start + note.at;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(level * note.gain, at);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + note.length);
      gain.connect(destination);
      if (note.kind === 'tone') {
        const oscillator = ctx.createOscillator();
        oscillator.type = note.wave;
        oscillator.frequency.setValueAtTime(note.frequency, at);
        if (note.to) oscillator.frequency.exponentialRampToValueAtTime(note.to, at + note.length);
        oscillator.connect(gain);
        oscillator.start(at);
        oscillator.stop(at + note.length + 0.01);
      } else {
        const source = ctx.createBufferSource();
        source.buffer = noiseBuffer;
        const filter = ctx.createBiquadFilter();
        filter.type = note.filter;
        filter.Q.value = note.q;
        filter.frequency.setValueAtTime(note.from * jitter, at);
        filter.frequency.exponentialRampToValueAtTime(note.to * jitter, at + note.length);
        source.connect(filter).connect(gain);
        source.start(at, Math.random() * 0.3);
        source.stop(at + note.length + 0.01);
      }
    }
  }

  function play(cue) {
    if (muted || !window.AudioContext) return;
    const now = performance.now();
    if (now - lastCueAt < repeatWindowMs) return;
    lastCueAt = now;
    // Browsers only start audio after a user gesture; every cue is triggered by one.
    context = context || new AudioContext();
    if (context.state === 'suspended') context.resume();
    renderCue(context, cue, context.currentTime, context.destination);
  }

  /** True when `target` is a parent of `current` (e.g. /collections → /collections/shirts). */
  function isUpward(target, current) {
    const up = target.pathname.replace(/\/$/, '') || '/';
    const here = current.pathname.replace(/\/$/, '') || '/';
    if (up === here) {
      // Same page: fewer query levels (e.g. ?shop=a/b → ?shop=a) is a step back.
      const depth = (url) => (url.searchParams.get('shop') || '').split('/').filter(Boolean).length;
      return depth(target) < depth(current);
    }
    return up === '/' ? here !== '/' : here.startsWith(`${up}/`);
  }

  function cueForClick(target) {
    if (!(target instanceof Element)) return null;
    const control = target.closest("a[href], button, summary, [role='button']");
    if (!control || control.matches("[disabled], [aria-disabled='true'], [data-sound-toggle]")) return null;
    const zone = control.closest('[data-sound]')?.dataset.sound;
    const link = control instanceof HTMLAnchorElement && control.target !== '_blank' && control.origin === window.location.origin ? new URL(control.href) : null;
    const upward = link ? isUpward(link, new URL(window.location.href)) : false;
    if (zone === 'flip') return 'flip';
    if (zone === 'folder') return upward ? 'folderClose' : 'folderOpen';
    if (zone && cues[zone]) return zone;
    // Shop panel back arrows and the product-view title link step back a level.
    if (control.matches('[data-back]') || control.closest('.bomex-shop-head--product .bomex-shop-title')) return 'back';
    if (/^(close|back|return)\b/i.test(control.getAttribute('aria-label') || '')) return 'back';
    if (link) return upward ? 'back' : 'forward';
    return 'select';
  }

  function renderToggle(toggle) {
    toggle.setAttribute('aria-pressed', String(!muted));
    toggle.querySelector('[data-sound-on]')?.toggleAttribute('hidden', muted);
    toggle.querySelector('[data-sound-off]')?.toggleAttribute('hidden', !muted);
  }

  function setMuted(next) {
    muted = next;
    try {
      window.localStorage.setItem(storageKey, next ? '1' : '0');
    } catch {
      // Storage can be unavailable (private windows); the choice then lasts for this page only.
    }
    document.querySelectorAll('[data-sound-toggle]').forEach(renderToggle);
  }

  document.addEventListener(
    'click',
    (event) => {
      const toggle = event.target instanceof Element ? event.target.closest('[data-sound-toggle]') : null;
      if (toggle) {
        const wasMuted = muted;
        setMuted(!muted);
        if (wasMuted) play('select');
        return;
      }
      const cue = cueForClick(event.target);
      if (cue) play(cue);
    },
    true
  );
  document.addEventListener(
    'change',
    (event) => {
      const field = event.target;
      if (!(field instanceof HTMLSelectElement || (field instanceof HTMLInputElement && (field.type === 'radio' || field.type === 'checkbox')))) return;
      play(field.closest('[data-sound="folder"]') ? 'folderOpen' : 'select');
    },
    true
  );
  // Browser Back/Forward: a parent page is "back", anything else counts as stepping forward.
  window.addEventListener('popstate', () => {
    const next = new URL(window.location.href);
    play(isUpward(next, lastUrl) ? 'back' : 'forward');
    lastUrl = next;
  });
  const rememberUrl = () => { lastUrl = new URL(window.location.href); };
  for (const method of ['pushState', 'replaceState']) {
    const original = history[method];
    history[method] = function (...args) {
      const result = original.apply(this, args);
      rememberUrl();
      return result;
    };
  }
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const somethingToClose =
      new URL(window.location.href).searchParams.has('shop') ||
      document.querySelector('dialog[open], details[open], .drawer.active, cart-drawer.active');
    if (somethingToClose) play('back');
  });

  window.BomexSounds = { cues: Object.keys(cues), renderCue, play };
  const init = () => document.querySelectorAll('[data-sound-toggle]').forEach(renderToggle);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
